import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useContent } from "../lib/content";
import { filterLinksForStage } from "../lib/lifecycle";
import { Home, LogOut, User as UserIcon, Menu, X, Eye, ExternalLink, ChevronDown } from "lucide-react";

function firstNameFrom(nameOrFirst, fullName) {
  if (nameOrFirst) return nameOrFirst;
  if (!fullName) return "";
  return fullName.trim().split(/\s+/)[0];
}

const LINKS_SELLER_BASE = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/prep-guide", label: "Prep Guide" },
  { to: "/process", label: "Process" },
  { to: "/dates", label: "Important Dates" },
  { to: "/activity", label: "Home Activity" },
  { to: "/forms", label: "Forms" },
  { to: "/net-sheet", label: "Net Sheet" },
  { to: "/vendors", label: "Vendors" },
  { to: "/documents", label: "Documents" },
  { to: "/closing", label: "Closing" },
  { to: "/ask", label: "__ASK__" }, // label resolved per-render so agent name flows through
];

const LINKS_ADMIN = [
  { to: "/admin", label: "Today" },
  { to: "/admin/clients", label: "Clients" },
  { to: "/admin/submissions", label: "Submissions" },
  { to: "/admin/messages", label: "Messages" },
];

// Set up once and then rarely touched, so these sit under the agent's own
// name instead of in the bar. The bar is for the work, the menu is for the
// setup; mixing the two is what made ten items feel like clutter, and the
// count was never really the problem.
const ADMIN_SETUP = [
  { to: "/admin/bundles", label: "Templates" },
  { to: "/admin/content", label: "Content" },
  { to: "/admin/billing", label: "Billing" },
];

// Nothing about one agent is baked in here. An agent whose Branding is
// still blank gets neutral wording, never another agent name or phone.
const AGENT_INFO = {
  name: "Your agent",
  phone: "",
  email: "",
  firm: "",
  first_name: "your agent",
};

export function useBranding() {
  const { content } = useContent();
  const b = content?.branding || {};
  return {
    product_name: b.product_name || "PorchLight",
    agent_name: b.agent_name || AGENT_INFO.name,
    agent_first_name: firstNameFrom(b.agent_first_name, b.agent_name) || AGENT_INFO.first_name,
    agent_email: b.agent_email || AGENT_INFO.email,
    agent_phone: b.agent_phone || AGENT_INFO.phone,
    firm: b.firm || AGENT_INFO.firm,
    primary_color: b.primary_color || "",
    logo_data_url: b.logo_data_url || "",
  };
}

export default function Layout({ children }) {
  const { user, logout, exitImpersonation, switchSide } = useAuth();
  const [acct, setAcct] = React.useState(false);
  const { preview } = useContent();
  const branding = useBranding();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const impersonating = user && user.impersonated_by;

  // The buyer front door — landing, login, register — brings its own chrome
  // (BuyerHub wordmark + split-screen auth), so skip the seller-app header/footer.
  const frontDoorPaths = ["/", "/login", "/register"];
  const isBuyerFrontDoor = !user && frontDoorPaths.includes(location.pathname);
  // Marketing site (theporchlight.io /for-agents) brings its own nav + footer too.
  const isMarketingRoute = location.pathname === "/for-agents";
  if (isBuyerFrontDoor || isMarketingRoute) {
    return <div style={{ background: "var(--bg)", minHeight: "100vh" }}>{children}</div>;
  }

  const exitPreview = () => {
    try { localStorage.removeItem("sellerhub_preview_content"); } catch {}
    try { window.sessionStorage.removeItem("sellerhub_preview_session"); } catch {}
    window.close();
    setTimeout(() => { window.location.href = "/admin/content"; }, 150);
  };

  const onExitImpersonation = async () => {
    await exitImpersonation();
    nav("/admin");
  };

  // Only show pages that apply to where this seller actually is. A pre-listing
  // seller staring at an empty Closing tab makes a real portal feel like a demo.
  const sellerLinks = filterLinksForStage(
    LINKS_SELLER_BASE.map((l) =>
      l.label === "__ASK__" ? { ...l, label: `Ask ${branding.agent_first_name}` } : l
    ),
    user?.lifecycle_status,
  );
  const adminLinks = LINKS_ADMIN;
  // Owner-only screens join the setup menu, not the bar. No agent has ever
  // seen these three anyway.
  const setupLinks = user?.is_primary_admin
    ? [...ADMIN_SETUP, { to: "/admin/marketing", label: "Marketing" }, { to: "/admin/broadcast", label: "Broadcast" }, { to: "/admin/insights", label: "Insights" }]
    : ADMIN_SETUP;
  // Buy-side clients get the BuyerHub tabbed experience which has its own
  // 11-tab nav. Showing the seller nav on top would be a redundant second
  // row — hide it and let BuyerHub own the navigation for these users.
  const onBuySide = user?.role === "seller" && (user?.active_side === "buy");
  const links = onBuySide
    ? []
    : (user?.role === "admin" && !preview && !impersonating ? adminLinks : sellerLinks);
  const isAuthed = !!user;

  // Inject primary color override (if the admin has chosen one) as a CSS variable at the
  // root wrapper. This cascades into every component that uses var(--primary).
  const rootStyle = { background: "var(--bg)", minHeight: "100vh" };
  if (branding.primary_color) rootStyle["--primary"] = branding.primary_color;

  return (
    <div style={rootStyle}>
      {impersonating && (
        <div data-testid="impersonate-banner" style={{ position: "sticky", top: 0, zIndex: 50, background: "var(--primary)", color: "#fff", padding: "10px 16px", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Eye size={14} /> Viewing as <b>{user.name}</b> ({user.email}) — this is exactly what they see.</span>
          <button data-testid="exit-impersonate-btn" onClick={onExitImpersonation} style={{ background: "#fff", color: "var(--primary)", border: "none", borderRadius: 9999, padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Exit & return to admin</button>
        </div>
      )}
      {preview && !impersonating && (
        <div data-testid="preview-banner" style={{ position: "sticky", top: 0, zIndex: 50, background: "var(--secondary)", color: "#fff", padding: "10px 16px", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Eye size={14} /> <b>Preview mode</b> — seeing your unsaved content edits. These changes are not live.</span>
          <button data-testid="exit-preview-btn" onClick={exitPreview} style={{ background: "#fff", color: "var(--secondary)", border: "none", borderRadius: 9999, padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Exit preview</button>
        </div>
      )}
      <header style={{ borderBottom: "1px solid var(--border)", background: "rgba(248,246,243,0.85)", backdropFilter: "blur(10px)", position: "sticky", top: 0, zIndex: 40 }}>
        <div className="container-x" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 72 }}>
          <button data-testid="brand-link" onClick={() => nav(isAuthed ? (user.role === "admin" ? "/admin" : "/dashboard") : "/")} className="btn-ghost" style={{ display: "flex", alignItems: "center", gap: 10, padding: 0 }}>
            {onBuySide ? (
              <span className="font-serif" style={{ fontSize: 24, fontWeight: 500, letterSpacing: "-0.01em" }}>BuyerHub</span>
            ) : (
              <>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "var(--primary)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                  {branding.logo_data_url ? (
                    <img src={branding.logo_data_url} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                  ) : (
                    <Home size={18} color="#fff" />
                  )}
                </div>
                <span className="font-serif" style={{ fontSize: 24, fontWeight: 500, letterSpacing: "-0.01em" }}>{branding.product_name}</span>
              </>
            )}
          </button>

          {isAuthed && (
            <nav className="nav-desktop">
              {links.map((l) => (
                <NavLink key={l.to} to={l.to} end={l.to === "/admin"} data-testid={`nav-${l.label.toLowerCase()}`} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                  {l.label}
                </NavLink>
              ))}
            </nav>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isAuthed && onBuySide && Array.isArray(user?.sides) && user.sides.some((s) => s.side === "sell") && (
              <button
                data-testid="selling-link"
                className="btn-ghost"
                onClick={async () => { const res = await switchSide("sell"); if (res?.ok) nav("/dashboard"); }}
                style={{ color: "var(--text-muted)", fontSize: 14, display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                Selling? <ExternalLink size={14} />
              </button>
            )}
            {isAuthed && !onBuySide && Array.isArray(user?.sides) && user.sides.length > 1 && user?.buyer_features_enabled && (
              <SideToggle
                sides={user.sides}
                active={user.active_side || "sell"}
                onChange={async (s) => {
                  const res = await switchSide(s);
                  if (res?.ok) {
                    // Route back to /dashboard so seller-only pages don't
                    // render buy-side data mid-flight. Same UX as a role change.
                    nav("/dashboard");
                  }
                }}
              />
            )}
            {isAuthed ? (
              <>
                {setupLinks.length > 0 && user?.role === "admin" ? (
                  <div style={{ position: "relative" }}>
                    <button
                      type="button"
                      className="btn-ghost nav-user-name"
                      data-testid="account-menu-toggle"
                      onClick={() => setAcct(!acct)}
                      style={{ display: "flex", alignItems: "center", gap: 8,
                               color: "var(--text-muted)", fontSize: 14 }}
                    >
                      <UserIcon size={16} />
                      <span data-testid="current-user-name">{user.name}</span>
                      <ChevronDown size={14} />
                    </button>
                    {acct && (
                      <>
                        {/* Deliberately no full-screen click catcher behind this. One made
                            clicking outside close the menu, but it also sat over the menu's
                            own links and swallowed them - the smoke walk caught it. The menu
                            closes on the toggle and on navigating, which is enough. */}
                        <div
                          data-testid="account-menu"
                          style={{ position: "absolute", right: 0, top: "calc(100% + 8px)",
                                   zIndex: 41, minWidth: 190, padding: 6, borderRadius: 12,
                                   background: "var(--bg)", border: "1px solid var(--border)",
                                   boxShadow: "0 12px 32px rgba(0,0,0,0.10)" }}
                        >
                          {setupLinks.map((l) => (
                            <NavLink
                              key={l.to}
                              to={l.to}
                              onClick={() => setAcct(false)}
                              data-testid={`nav-${l.label.toLowerCase()}`}
                              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                              style={{ display: "block", padding: "8px 10px", borderRadius: 8, fontSize: 14 }}
                            >
                              {l.label}
                            </NavLink>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="nav-user-name" style={{ alignItems: "center", gap: 8, color: "var(--text-muted)", fontSize: 14, display: "flex" }}>
                    <UserIcon size={16} />
                    <span data-testid="current-user-name">{user.name}</span>
                  </div>
                )}
                <button data-testid="logout-btn" className="btn-ghost" onClick={async () => { await logout(); nav("/"); }} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <LogOut size={16} />{onBuySide && <span style={{ fontSize: 14 }}>Sign out</span>}
                </button>
                <button className="btn-ghost nav-mobile-btn" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
                  {open ? <X size={20} /> : <Menu size={20} />}
                </button>
              </>
            ) : (
              <>
                <button data-testid="login-btn-nav" className="btn-ghost" onClick={() => nav("/login")}>Log in</button>
                <button data-testid="get-started-btn-nav" className="btn-primary" onClick={() => nav("/register")}>Get Started</button>
              </>
            )}
          </div>
        </div>

        {isAuthed && open && (
          <div style={{ borderTop: "1px solid var(--border)", padding: "12px 24px", background: "var(--surface)" }}>
            {/* The phone menu carries the setup links too. The account menu
                they normally live in is display:none under 1080px, so without
                this an agent on a phone could not reach Billing at all. */}
            {(user?.role === "admin" ? [...links, ...setupLinks] : links).map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/admin"} onClick={() => setOpen(false)} data-testid={`nav-mobile-${l.label.toLowerCase()}`} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} style={{ display: "block", padding: "10px 8px" }}>
                {l.label}
              </NavLink>
            ))}
          </div>
        )}
      </header>

      <main className="page-fade">{children}</main>

      <footer style={{ marginTop: 80, borderTop: "1px solid var(--border)", padding: "40px 0 32px", color: "var(--text-muted)", fontSize: 14 }}>
        <div className="container-x footer-grid">
          <div>
            <div className="font-serif" style={{ fontSize: 22, color: "var(--text)", fontWeight: 500, letterSpacing: "-0.01em" }}>{branding.agent_name}</div>
            {branding.firm && <div style={{ marginTop: 4 }}>{branding.firm}</div>}
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 4 }}>
              {branding.agent_phone && <a href={`tel:${(branding.agent_phone || "").replace(/\D/g, "")}`} style={{ color: "var(--text)", fontWeight: 500 }}>{branding.agent_phone}</a>}
              {branding.agent_email && <a href={`mailto:${branding.agent_email}`} style={{ color: "var(--text)" }}>{branding.agent_email}</a>}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div>© {new Date().getFullYear()} {branding.product_name}</div>
            <div style={{ marginTop: 4 }}>Your companion for a smooth home sale.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}


// Pill toggle for clients who are both selling AND buying. Renders as two
// segmented buttons (SELLING · BUYING) with the active one in solid green.
// Only mounted when user.sides.length > 1 so single-side clients never
// see it — they just see their own portal without any toggle chrome.
function SideToggle({ sides, active, onChange }) {
  const labelFor = (s) => (s === "buy" ? "BUYING" : "SELLING");
  return (
    <div
      data-testid="side-toggle"
      role="tablist"
      style={{
        display: "inline-flex", alignItems: "center",
        background: "var(--surface-alt)",
        border: "1px solid var(--border)", borderRadius: 999,
        padding: 3, marginRight: 6,
      }}
    >
      {sides.map((s) => {
        const isActive = s.side === active;
        return (
          <button
            key={s.side}
            type="button"
            role="tab"
            aria-selected={isActive}
            data-testid={`side-toggle-${s.side}`}
            onClick={() => !isActive && onChange(s.side)}
            style={{
              border: "none", cursor: isActive ? "default" : "pointer",
              padding: "5px 14px", borderRadius: 999,
              fontSize: 11, fontWeight: 700, letterSpacing: "0.10em",
              background: isActive ? "var(--primary)" : "transparent",
              color: isActive ? "#fff" : "var(--text-muted)",
              transition: "all 120ms ease",
            }}
          >
            {labelFor(s.side)}
          </button>
        );
      })}
    </div>
  );
}
